import React, { useState, useEffect, useContext } from 'react';
import {
  Container,
  Tabs,
  Tab,
  Badge,
  Button,
  Spinner,
  Alert,
  Pagination,
  InputGroup,
  Form
} from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../../context/AuthContext';
import RejectModal from './RejectModal';
import { getUploadPhase, formatCountdown } from '../../utils/examWindow';

function formatDate(dateString) {
  if (!dateString) return '';
  const dateObj = new Date(dateString);
  return dateObj.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
}

// Deprecated: replaced by explicit Team Leader and Examiners fields in job details
const getExaminerName = (job) => {
  if (job.teamLeader && (job.teamLeader.fullName || job.teamLeader.username)) {
    return job.teamLeader.fullName || job.teamLeader.username;
  }
  if (job.examiners && job.examiners.length > 0) {
    return job.examiners[0].fullName || job.examiners[0].username;
  }
  return job.examinerName || 'N/A';
};

function JobsDashboard() {
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 6; // cards per page

  // Reset page on tab or search change
  
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [allJobs, setAllJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [activeTab, setActiveTab] = useState('Pending');

  // State for rejection modal
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedJobForRejection, setSelectedJobForRejection] = useState(null);
  // State to store search terms for each tab
  const [searchTerms, setSearchTerms] = useState({
   Pending: '',
   'In-progress': '',
   Completed: '',
   Rejected: ''
  });

  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, searchTerms[activeTab]]);


// Handle search input change
  const handleSearchChange = (tabKey, value) => {
   setSearchTerms(prev => ({ ...prev, [tabKey]: value }));
  };

  // Ticks every second so the In-progress countdown updates live and the
  // Upload button activates on its own the instant the window opens,
  // without needing a manual refresh or other interaction to re-render.
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const interval = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  // Delegates to the shared exam-window utility (also used by ExamUploadPage)
  // so both pages agree on exactly when uploading is allowed.
  const getJobUploadPhase = (job) => getUploadPhase(job, now);
  const isUploadAllowed = (job) => getJobUploadPhase(job).phase === 'during';

  const fetchJobs = async (silent = false) => {
    console.log('Fetching jobs... Current user:', user);
    if (!user?.username) {
      console.log('No user data available yet');
      return;
    }

    if (!silent) setLoading(true);
    try {
      // Cache-busting query param: this endpoint is polled every 30s so a
      // reschedule shows up promptly, but the URL is otherwise identical on
      // every call - an intermediate proxy that ignores our Cache-Control:
      // no-store header (some institutional networks do) would key its
      // cache purely on the URL and keep serving the first response
      // forever. A unique query string defeats that regardless of cause.
      const response = await axios.get('/api/exam-assignments', {
        params: { _: Date.now() },
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      console.log('Raw jobs data:', response.data);
      setAllJobs(response.data);
    } catch (err) {
      console.error('Error fetching assignments:', err);
      if (!silent) setError('Error fetching assignments.');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  // Fetch jobs when component mounts
  useEffect(() => {
    console.log('Component mounted, user:', user);
    fetchJobs();
  }, []);

  // Poll periodically (without the loading spinner) so an admin-side change
  // - e.g. a rescheduled exam date/time - shows up here, and the upload
  // window/countdown recalculates against it, without a manual refresh.
  useEffect(() => {
    const interval = setInterval(() => fetchJobs(true), 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line
  }, [user]);

  // Fetch jobs when user data changes
  useEffect(() => {
    console.log('User data changed:', user);
    if (user?.username) {
      fetchJobs();
    }
  }, [user]);

  const isUserExaminer = (job) => {
    console.log('Checking examiner for job:', job);
    console.log('Current user:', user);
    if (!user?.username) {
      console.log('No user data available for examiner check');
      return false;
    }
    const isExaminer = job.examiners?.some(e => e.username.toLowerCase() === user.username.toLowerCase());
    console.log('Is examiner:', isExaminer);
    return isExaminer;
  };

  const pendingJobs = allJobs.filter(
    (job) => {
      const isPending = job.status.toUpperCase() === 'ASSIGNED' && isUserExaminer(job);
      console.log('Job pending check:', { jobId: job.id, status: job.status, isPending });
      return isPending;
    }
  );
  
  const inProgressJobs = allJobs.filter(
    (job) => {
      const status = job.status.toUpperCase();
      const isInProgress = (status === 'ACCEPTED_BY_EXAMINER' || status === 'WORK_UNDER_REVIEW') && isUserExaminer(job);
      console.log('Job in-progress check:', { jobId: job.id, status: job.status, isInProgress });
      return isInProgress;
    }
  );
  
  const completedJobs = allJobs.filter(
    (job) => {
      const isCompleted = job.status.toUpperCase() === 'COMPLETED' && isUserExaminer(job);
      console.log('Job completed check:', { jobId: job.id, status: job.status, isCompleted });
      return isCompleted;
    }
  );

  const rejectedJobs = allJobs.filter(
    (job) => {
      const status = job.status.toUpperCase();
      const isRejected = (status === 'REJECTED_BY_EXAMINER' || status === 'REJECTED') && isUserExaminer(job);
      console.log('Job Rejected check:', { jobId: job.id, status: job.status, isRejected });
      return isRejected;
    }
  );

  // Debug logging
  useEffect(() => {
    console.log('State update:', {
      user,
      allJobs,
      pendingJobs,
      inProgressJobs,
      completedJobs
    });
  }, [user, allJobs, pendingJobs, inProgressJobs, completedJobs]);

  // Only the designated team leader may accept/reject a job assigned to more
  // than one examiner - mirrors the check the backend enforces in
  // ExamAssignmentService.respondToAssignment.
  const isTeamLeader = (job) => {
    if (!user?.id) return false;
    if (!job.examiners || job.examiners.length <= 1) return true; // solo job, no restriction
    return !!job.teamLeader && job.teamLeader.id === user.id;
  };

  const getTeamLeaderName = (job) => {
    if (job.teamLeader) return job.teamLeader.fullName || job.teamLeader.username;
    return 'your team leader';
  };

  const handleAccept = async (job) => {
    if (!isTeamLeader(job)) {
      setActionError(`Only the team leader (${getTeamLeaderName(job)}) is allowed to accept or reject this job.`);
      return;
    }
    try {
      setActionError(null);
      console.log("Accepting jobId:", job.id, "with examinerId:", user.id);
      await axios.post(`/api/exam-assignments/${job.id}/respond?examinerId=${user.id}&accept=true`);
      setAllJobs((prev) =>
        prev.map(j => (j.id === job.id ? { ...j, status: 'ACCEPTED_BY_EXAMINER' } : j))
      );
    } catch (err) {
      setActionError(
        err.response?.data?.error || err.response?.data?.message || 'Error accepting assignment.'
      );
      console.error(err);
    }
  };

  const openRejectModal = (job) => {
    if (!isTeamLeader(job)) {
      setActionError(`Only the team leader (${getTeamLeaderName(job)}) is allowed to accept or reject this job.`);
      return;
    }
    setSelectedJobForRejection(job);
    setShowRejectModal(true);
  };

  const handleRejectSubmit = async (reason) => {
    try {
      setActionError(null);
      await axios.post(`/api/exam-assignments/${selectedJobForRejection.id}/respond?examinerId=${user.id}&accept=false&rejectionReason=${encodeURIComponent(reason)}`);
      setAllJobs((prev) =>
        prev.map(j =>
          j.id === selectedJobForRejection.id ? { ...j, status: 'Rejected' } : j
        )
      );
      setShowRejectModal(false);
      setSelectedJobForRejection(null);
    } catch (err) {
      setActionError(
      err.response?.data?.error || err.response?.data?.message || 'Error rejecting assignment.'
    );
    }
  };

  const handleUpload = async (job) => {
    try {
      const response = await axios.get(`/api/modules/${job.moduleId}`);
      const moduleDetails = response.data;
      navigate(`/examiner/job/${job.id}/upload`, { 
        state: { 
          module: moduleDetails,
          examCenterId: job.examCentreId 
        } 
      });
    } catch (error) {
      setError("Error fetching module details for upload.");
    }
  };

  // Rendering for each list
  const renderJobsList = (jobsList, tabKey) => {

    const searchTerm = searchTerms[tabKey]?.toLowerCase() || '';

    // Filter jobs based on search term
    const filteredJobs = jobsList.filter(job => {
    const moduleCode = job.moduleCode?.toLowerCase() || '';
    const moduleName = job.moduleName?.toLowerCase() || '';
    const seriesName = (job.examSeriesName || job.examSeries?.name || '').toLowerCase();

    return (
      moduleCode.includes(searchTerm) ||
      moduleName.includes(searchTerm) ||
      seriesName.includes(searchTerm)
    );
  });
    // Pagination logic
    const totalPages = Math.ceil(filteredJobs.length / rowsPerPage);
    const paginatedJobs = filteredJobs.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

    if (loading) {
      return (
        <div className="text-center my-3">
          <Spinner animation="border" variant="primary" />
        </div>
      );
    }
    if (error) return <Alert variant="danger">{error}</Alert>;
    if (jobsList.length === 0)
      return <Alert variant="info">Nothing to show here</Alert>;

    return (
      <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
        {actionError && <Alert variant="danger">{actionError}</Alert>}
        {jobsList.length > 10 && (
          <InputGroup className="mb-3">
            <Form.Control
              placeholder="Search by module code, module name, or series..."
              value={searchTerms[tabKey]}
              onChange={(e) => handleSearchChange(tabKey, e.target.value)}
            />
          </InputGroup>
        )}
        <ol className="list-unstyled">
          {paginatedJobs.map((job, index) => {
            // Continuous numbering across pages
            const cardNumber = (currentPage - 1) * rowsPerPage + index + 1;
            const moduleName = job.moduleName || 'N/A';
            const examDate = job.examDate ? formatDate(job.examDate) : 'Not Provided';
            const examCentre = job.examCentreName || 'N/A';
            const assignedDate = job.assignedDate ? formatDate(job.assignedDate) : 'Not Provided';

            let showReject = false;
            let showAccept = false;
            let showUpload = false;
            let showWorkUnderReview = false;
            let showReupload = false;

            if (job.status.toUpperCase() === 'ASSIGNED') {
              showReject = true;
              showAccept = true;
            } else if (job.status.toUpperCase() === 'ACCEPTED_BY_EXAMINER') {
              showUpload = true;
            } else if (job.status.toUpperCase() === 'WORK_UNDER_REVIEW') {
              showWorkUnderReview = true;
            } else if (job.status.toUpperCase() === 'REJECTED') {
              // Show re-upload button only if current time is within exam window
              showReupload = isUploadAllowed(job);
            }
            const isCompleted = job.status.toUpperCase() === 'COMPLETED';
            const uploadAllowed = isUploadAllowed(job);

            return (
              <li key={job.id} className="mb-3">
                <div style={{ border: '1px solid #ddd', borderRadius: '8px', padding: '1rem' }}>
                  <div className="d-flex">
                    <div className="me-3 fw-bold" style={{ fontSize: '1.2rem', minWidth: '2rem' }}>
                      {cardNumber}
                    </div>
                    <div className="flex-grow-1">
                      <div className="d-flex flex-wrap justify-content-between align-items-start gap-1">
                        <div>
                          <h5 className="mb-1 text-break">Exam: {moduleName}</h5>
                        </div>
                        <div className="fw-bold" style={{ fontSize: '1rem' }}>
                          Module code: {job.moduleCode}
                        </div>
                      </div>
                      <div className="mt-2" style={{ fontSize: '0.95rem' }}>
                        <div>Exam Date: {examDate}</div>
                        <div>Exam Centre: {examCentre}</div>
                        <div>Assigned Date: {assignedDate}</div>
                        <div>Status: {job.status}</div>
                        {job.status.toUpperCase() === 'REJECTED' && job.rejectionReason && (
                          <div style={{ color: '#dc3545', fontWeight: 'bold', marginTop: '5px' }}>
                            Rejection Reason: {job.rejectionReason}
                          </div>
                        )}
                        <div>Exam Series: {job.examSeriesName || job.examSeries?.name || 'N/A'}</div>
                        <div>Number of Students: {job.numberOfStudents}</div>
                        {(() => {
                           // Determine team leader and other examiners
                              let teamLeader = null;
                              let otherExaminers = [];
                              if (job.teamLeader) {
                               teamLeader = job.teamLeader.fullName || job.teamLeader.username || job.teamLeader;
                              if (job.examiners && job.examiners.length > 0) {
                               otherExaminers = job.examiners.filter(ex => (ex.id !== job.teamLeader.id)).map(ex => ex.fullName || ex.username || ex);
                             }
                           } else if (job.examiners && job.examiners.length > 0) {
                              teamLeader = job.examiners[0].fullName || job.examiners[0].username || job.examiners[0];
                              otherExaminers = job.examiners.slice(1).map(ex => ex.fullName || ex.username || ex);
                         }
  return (
    <>
      <div>
        <strong>Team Leader:</strong> {teamLeader || 'N/A'}
        {job.examiners && job.examiners.length > 1 && isTeamLeader(job) && (
          <Badge bg="success" className="ms-2">You are the Team Leader</Badge>
        )}
      </div>
      {otherExaminers.length > 0 && (
        <div><strong>Examiners:</strong> {otherExaminers.join(', ')}</div>
      )}
    </>
  );
})()}
                        {job.examStartTime && job.examDurationMinutes && (
                          <div>
                            Exam Time: {job.examStartTime} (Duration: {job.examDurationMinutes} minutes)
                          </div>
                        )}
                        {showUpload && (() => {
                          const phase = getJobUploadPhase(job);
                          if (phase.phase === 'unknown') return null;
                          const color = phase.phase === 'during' ? '#198754' : phase.phase === 'before' ? '#fd7e14' : '#dc3545';
                          return (
                            <div className="mt-1" style={{ fontWeight: 'bold', color }}>
                              {phase.phase === 'before' && phase.window && (
                                <>Upload opens in: {formatCountdown(phase.window.start - now)}</>
                              )}
                              {phase.phase === 'during' && phase.window && (
                                <>Upload window closes in: {formatCountdown(phase.window.end - now)}</>
                              )}
                              {phase.phase === 'after' && (
                                <>Upload window has closed.</>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                      <div className="d-flex flex-wrap justify-content-end gap-2 mt-3">
                        {showReject && (
                          <Button variant="danger" onClick={() => openRejectModal(job)}>
                            Reject
                          </Button>
                        )}
                        {showAccept && (
                          <Button variant="primary" onClick={() => handleAccept(job)}>
                            Accept
                          </Button>
                        )}
                        {showUpload && (
                          <Button variant="primary" onClick={() => handleUpload(job)} disabled={!uploadAllowed}
                        >
                            Upload
                          </Button>
                        )}
                        {showReupload && (
                          <Button variant="warning" onClick={() => handleUpload(job)}>
                            Re-upload
                          </Button>
                        )}
                        {showWorkUnderReview && (
                          <Button variant="secondary" disabled>
                            Work Under Review
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </li>
            );
          })}
        </ol>
        <div style={{ position: 'sticky', bottom: 0, background: '#fff', zIndex: 10, paddingTop: 8 }}>
          <Pagination className="justify-content-center mb-0 flex-wrap" style={{ rowGap: '0.25rem' }}>
            <Pagination.Prev
              onClick={() => setCurrentPage(c => Math.max(1, c - 1))}
              disabled={currentPage === 1 || totalPages === 1}
            />
            {totalPages === 1 ? (
              <Pagination.Item active disabled>1</Pagination.Item>
            ) : (
              [...Array(totalPages)].map((_, idx) => (
                <Pagination.Item
                  key={idx + 1}
                  active={idx + 1 === currentPage}
                  onClick={() => setCurrentPage(idx + 1)}
                >
                  {idx + 1}
                </Pagination.Item>
              ))
            )}
            <Pagination.Next
              onClick={() => setCurrentPage(c => Math.min(totalPages, c + 1))}
              disabled={currentPage === totalPages || totalPages === 1}
            />
          </Pagination>
        </div>
      </div>
    );
  };

  return (
    <Container fluid className="mt-3">
      <Tabs
        id="jobs-tabs"
        activeKey={activeTab}
        onSelect={(k) => {
          setActiveTab(k);
        }}
        variant="pills"
        className="mb-3"
        justify
      >
        <Tab eventKey="Pending" title={<><span>Pending</span> <Badge bg="secondary">{pendingJobs.length}</Badge></>}>
          {renderJobsList(pendingJobs, 'Pending')}
        </Tab>
        <Tab eventKey="In-progress" title={<><span>In-progress</span> <Badge bg="secondary">{inProgressJobs.length}</Badge></>}>
          {renderJobsList(inProgressJobs, 'In-progress')}
        </Tab>
        <Tab eventKey="Completed" title={<><span>Completed</span> <Badge bg="secondary">{completedJobs.length}</Badge></>}>
          {renderJobsList(completedJobs, 'Completed')}
        </Tab>
        <Tab eventKey="Rejected" title={<><span>Rejected</span> <Badge bg="secondary">{rejectedJobs.length}</Badge></>}>
          {renderJobsList(rejectedJobs, 'Rejected')}
        </Tab>
      </Tabs>
      {showRejectModal && (
        <RejectModal
          show={showRejectModal}
          onHide={() => {
            setShowRejectModal(false);
            setSelectedJobForRejection(null);
          }}
          onSubmit={handleRejectSubmit}
        />
      )}
    </Container>
  );
}

export default JobsDashboard;
